import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TiptapEditor from '@/components/TiptapEditor';
import ImageUploader from '@/components/ImageUploader';
import { ArrowLeft, Save, Home, Plus, Trash2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableSection = ({ id, section, onUpdate, onDelete, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="admin-card mb-4">
      <div className="flex items-start gap-2">
        <button {...attributes} {...listeners} className="p-2 cursor-move hover:bg-opacity-20 hover:bg-gray-500 rounded">
          <GripVertical size={20} style={{ color: 'var(--admin-text-secondary)' }} />
        </button>
        <div className="flex-1">
          {children}
        </div>
        <button
          onClick={onDelete}
          className="p-2 rounded transition-colors"
          style={{ color: 'var(--admin-error)' }}
          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <Trash2 size={20} />
        </button>
      </div>
    </div>
  );
};

const AdminHomePage = () => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    hero_title: 'Добро пожаловать',
    hero_title_size: '3rem',
    hero_subtitle: '',
    hero_subtitle_size: '1.25rem',
    hero_image: '',
    sections: [],
    use_blocks: false,
    blocks: []
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/home-content`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch home content:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/home-content`, content);
      toast.success('Главная страница обновлена');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: '',
      content: '',
      image: '',
      type: 'text' // text, image-text, cards
    };
    setContent({ ...content, sections: [...content.sections, newSection] });
  };

  const updateSection = (sectionId, updates) => {
    setContent({
      ...content,
      sections: content.sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    });
  };

  const deleteSection = (sectionId) => {
    setContent({
      ...content,
      sections: content.sections.filter(section => section.id !== sectionId)
    });
  };
  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      order: content.blocks.length,
      column_span: 3,
      content: getDefaultBlockContent(type)
    };
    setContent({ ...content, blocks: [...content.blocks, newBlock] });
  };
  const getDefaultBlockContent = (type) => {
    switch (type) {
      case 'heading': return { text: '' };
      case 'text': return { html: '' };
      case 'image': return { url: '', alt: '', caption: '' };
      case 'quote': return { text: '', author: '' };
      case 'video': return { url: '' };
      case 'html': return { code: '' };
      case 'services': return { selectedServices: [] };
      case 'divider': return { style: 'solid' };
      case 'button': return { text: 'Кнопка', link: '#' };
      case 'cards': return { items: [{ title: 'Карточка 1', text: 'Описание', icon: 'Star' }] };
      case 'accordion': return { items: [{ title: 'Вопрос 1', content: 'Ответ 1' }] };
      case 'contact_info': return {
        email: '',
        whatsapp: '',
        telegram: '',
        social_links: []
      };
      case 'tarot_card': return { card_name: 'Карта Дня', description: '' };
      case 'astro_widget': return { widget_type: 'moon_phase' };
      case 'calendar': return { title: 'Запись на консультацию' };
      default: return {};
    }
  };
  const updateBlock = (blockId, updates) => {
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId ? { ...block, ...updates } : block
      )
    });
  };
  const deleteBlock = (blockId) => {
    setContent({
      ...content,
      blocks: content.blocks.filter(block => block.id !== blockId)
    });
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = content.sections.findIndex(s => s.id === active.id);
      const newIndex = content.sections.findIndex(s => s.id === over.id);
      const reordered = arrayMove(content.sections, oldIndex, newIndex);
      setContent({ ...content, sections: reordered });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--admin-bg-primary)', color: 'var(--admin-text-primary)' }}>
      <div className="container mx-auto max-w-4xl px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--admin-text-primary)' }}>
              <Home className="inline mr-2" size={36} />
              Редактирование Главной Страницы
            </h1>
          </div>
          <Button onClick={handleSave} disabled={loading} className="admin-button" data-testid="save-home-content">
            <Save className="mr-2" size={18} />
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Hero Section */}
          <Card className="admin-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--admin-text-primary)' }}>Hero Секция</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="admin-label">
                  Главный Заголовок
                </label>
                <Input
                  data-testid="hero-title-input"
                  value={content.hero_title}
                  onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                  placeholder="Добро пожаловать"
                  className="admin-input"
                />
                <div className="mt-2">
                  <label className="admin-label text-sm">
                    Размер шрифта заголовка
                  </label>
                  <Select
                    value={content.hero_title_size || '3rem'}
                    onValueChange={(value) => setContent({ ...content, hero_title_size: value })}
                  >
                    <SelectTrigger className="admin-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="admin-select-content">
                      <SelectItem value="2rem" className="admin-select-item">Маленький (2rem)</SelectItem>
                      <SelectItem value="2.5rem" className="admin-select-item">Средний (2.5rem)</SelectItem>
                      <SelectItem value="3rem" className="admin-select-item">Большой (3rem)</SelectItem>
                      <SelectItem value="3.5rem" className="admin-select-item">Очень большой (3.5rem)</SelectItem>
                      <SelectItem value="4rem" className="admin-select-item">Огромный (4rem)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="admin-label">
                  Подзаголовок
                </label>
                <Textarea
                  data-testid="hero-subtitle-input"
                  value={content.hero_subtitle}
                  onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                  placeholder="Краткое описание"
                  rows={3}
                  className="admin-textarea"
                />
                <div className="mt-2">
                  <label className="admin-label text-sm">
                    Размер шрифта подзаголовка
                  </label>
                  <Select
                    value={content.hero_subtitle_size || '1.25rem'}
                    onValueChange={(value) => setContent({ ...content, hero_subtitle_size: value })}
                  >
                    <SelectTrigger className="admin-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="admin-select-content">
                      <SelectItem value="0.875rem" className="admin-select-item">Маленький (0.875rem)</SelectItem>
                      <SelectItem value="1rem" className="admin-select-item">Средний (1rem)</SelectItem>
                      <SelectItem value="1.25rem" className="admin-select-item">Большой (1.25rem)</SelectItem>
                      <SelectItem value="1.5rem" className="admin-select-item">Очень большой (1.5rem)</SelectItem>
                      <SelectItem value="1.75rem" className="admin-select-item">Огромный (1.75rem)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="admin-label">
                  URL Изображения Hero
                </label>
                <Input
                  data-testid="hero-image-input"
                  value={content.hero_image}
                  onChange={(e) => setContent({ ...content, hero_image: e.target.value })}
                  placeholder="https://example.com/hero-image.jpg"
                  className="admin-input"
                />
                {content.hero_image && (
                  <img
                    src={content.hero_image}
                    alt="Hero preview"
                    className="mt-3 w-full max-h-64 object-cover rounded-lg"
                  />
                )}
              </div>
            </CardContent>
          </Card>
          {/* Toggle between systems */}
          <Card className="admin-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--admin-text-primary)' }}>Режим редактирования</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Switch
                  checked={content.use_blocks}
                  onCheckedChange={(checked) => setContent({ ...content, use_blocks: checked })}
                />
                <div>
                  <label style={{ color: 'var(--admin-text-primary)', fontWeight: '600' }}>
                    {content.use_blocks ? 'Расширенный редактор блоков' : 'Простые секции'}
                  </label>
                  <p className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>
                    {content.use_blocks
                      ? 'Используйте полный редактор с 15 типами блоков, колонками и drag & drop'
                      : 'Простой редактор с текстовыми секциями'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Editor - conditional rendering */}
          {!content.use_blocks ? (
            // Simple sections (legacy)
            <Card className="admin-card">
              <CardHeader>
                <CardTitle style={{ color: 'var(--admin-text-primary)' }}>Контентные Секции (Простой режим)</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={addSection}
                  variant="outline"
                  className="mb-4"
                  data-testid="add-section-button"
                >
                  <Plus className="mr-2" size={18} />
                  Добавить Секцию
                </Button>
                {content.sections.length === 0 ? (
                  <p style={{ color: 'var(--admin-text-secondary)' }} className="text-center py-8">
                    Нет секций. Добавьте первую!
                  </p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={content.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                      {content.sections.map((section) => (
                        <SortableSection
                          key={section.id}
                          id={section.id}
                          section={section}
                          onUpdate={updateSection}
                          onDelete={() => deleteSection(section.id)}
                        >
                          <div className="space-y-3">
                            <Input
                              data-testid={`section-title-${section.id}`}
                              placeholder="Заголовок секции"
                              value={section.title}
                              onChange={(e) => updateSection(section.id, { title: e.target.value })}
                              className="admin-input"
                            />
                            <Textarea
                              data-testid={`section-content-${section.id}`}
                              placeholder="Содержание секции"
                              value={section.content}
                              onChange={(e) => updateSection(section.id, { content: e.target.value })}
                              rows={4}
                              className="admin-textarea"
                            />
                            <Input
                              data-testid={`section-image-${section.id}`}
                              placeholder="URL изображения (необязательно)"
                              value={section.image || ''}
                              onChange={(e) => updateSection(section.id, { image: e.target.value })}
                              className="admin-input"
                            />
                            {section.image && (
                              <img
                                src={section.image}
                                alt="Section preview"
                                className="w-full max-h-48 object-cover rounded"
                              />
                            )}
                          </div>
                        </SortableSection>
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          ) : (
            // Advanced block editor
            <Card className="admin-card">
              <CardHeader>
                <CardTitle style={{ color: 'var(--admin-text-primary)' }}>Редактор Блоков (Расширенный режим)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <label className="admin-label mb-3">Добавить блок:</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { type: 'heading', label: '📝 Заголовок' },
                      { type: 'text', label: '📄 Текст' },
                      { type: 'image', label: '🖼️ Изображение' },
                      { type: 'quote', label: '💬 Цитата' },
                      { type: 'video', label: '🎥 Видео' },
                      { type: 'button', label: '🔘 Кнопка' },
                      { type: 'divider', label: '➖ Разделитель' },
                      { type: 'services', label: '💼 Услуги' },
                      { type: 'cards', label: '🃏 Карточки' },
                      { type: 'accordion', label: '📋 Аккордеон' },
                      { type: 'contact_info', label: '📞 Контакты' },
                      { type: 'tarot_card', label: '🔮 Таро' },
                      { type: 'astro_widget', label: '⭐ Астро' },
                      { type: 'calendar', label: '📅 Календарь' },
                      { type: 'html', label: '💻 HTML' },
                    ].map(({ type, label }) => (
                      <Button
                        key={type}
                        onClick={() => addBlock(type)}
                        variant="outline"
                        size="sm"
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
                {content.blocks.length === 0 ? (
                  <p style={{ color: 'var(--admin-text-secondary)' }} className="text-center py-8">
                    Нет блоков. Добавьте первый блок выше!
                  </p>
                ) : (
                  <div className="space-y-4">
                    {content.blocks.map((block) => (
                      <div key={block.id} className="admin-card p-4">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm font-medium px-2 py-1 rounded" style={{ background: 'var(--admin-button-primary-bg)', color: 'var(--admin-button-primary-text)' }}>
                            {block.type}
                          </span>
                          <button
                            onClick={() => deleteBlock(block.id)}
                            className="p-2 rounded transition-colors"
                            style={{ color: 'var(--admin-error)' }}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>

                        {/* Render block editor based on type */}
                        {block.type === 'heading' && (
                          <Input
                            placeholder="Введите заголовок"
                            value={block.content.text || ''}
                            onChange={(e) => updateBlock(block.id, { content: { text: e.target.value } })}
                            className="admin-input text-2xl font-bold"
                          />
                        )}

                        {block.type === 'text' && (
                          <TiptapEditor
                            value={block.content.html || ''}
                            onChange={(html) => updateBlock(block.id, { content: { html } })}
                          />
                        )}

                        {block.type === 'image' && (
                          <div className="space-y-2">
                            <ImageUploader
                              currentImageUrl={block.content.url}
                              onImageUploaded={(url) => updateBlock(block.id, { content: { ...block.content, url } })}
                            />
                            <Input
                              placeholder="Alt текст"
                              value={block.content.alt || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, alt: e.target.value } })}
                              className="admin-input"
                            />
                          </div>
                        )}

                        {block.type === 'button' && (
                          <div className="space-y-2">
                            <Input
                              placeholder="Текст кнопки"
                              value={block.content.text || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, text: e.target.value } })}
                              className="admin-input"
                            />
                            <Input
                              placeholder="URL ссылки"
                              value={block.content.link || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, link: e.target.value } })}
                              className="admin-input"
                            />
                          </div>
                        )}

                        {block.type === 'services' && (
                          <div className="p-4 rounded" style={{ background: 'var(--admin-bg-secondary)' }}>
                            <p style={{ color: 'var(--admin-text-secondary)' }}>
                              Блок "Услуги" будет отображать все активные услуги
                            </p>
                          </div>
                        )}

                        {block.type === 'contact_info' && (
                          <div className="space-y-4">
                            <div>
                              <label className="admin-label">Email</label>
                              <Input
                                placeholder="example@email.com"
                                value={block.content.email || ''}
                                onChange={(e) => updateBlock(block.id, { content: { ...block.content, email: e.target.value } })}
                                className="admin-input"
                              />
                            </div>

                            <div>
                              <label className="admin-label">WhatsApp</label>
                              <Input
                                placeholder="+7 999 123-45-67"
                                value={block.content.whatsapp || ''}
                                onChange={(e) => updateBlock(block.id, { content: { ...block.content, whatsapp: e.target.value } })}
                                className="admin-input"
                              />
                            </div>

                            <div>
                              <label className="admin-label">Telegram</label>
                              <Input
                                placeholder="@username"
                                value={block.content.telegram || ''}
                                onChange={(e) => updateBlock(block.id, { content: { ...block.content, telegram: e.target.value } })}
                                className="admin-input"
                              />
                            </div>

                            <div className="border-t pt-4" style={{ borderColor: 'var(--admin-border)' }}>
                              <div className="flex justify-between items-center mb-3">
                                <label className="admin-label mb-0">Социальные сети</label>
                                <Button
                                  onClick={() => {
                                    const socialLinks = block.content.social_links || [];
                                    updateBlock(block.id, { content: { ...block.content, social_links: [...socialLinks, { platform: 'instagram', url: '', label: '' }] } });
                                  }}
                                  size="sm"
                                  variant="outline"
                                >
                                  <Plus size={16} className="mr-1" />
                                  Добавить
                                </Button>
                              </div>

                              {(block.content.social_links || []).map((link, index) => (
                                <div key={index} className="flex gap-2 mb-3 p-3 rounded" style={{ background: 'var(--admin-bg-secondary)' }}>
                                  <div className="flex-1 space-y-2">
                                    <Select
                                      value={link.platform}
                                      onValueChange={(value) => {
                                        const socialLinks = [...(block.content.social_links || [])];
                                        socialLinks[index] = { ...socialLinks[index], platform: value };
                                        updateBlock(block.id, { content: { ...block.content, social_links: socialLinks } });
                                      }}
                                    >
                                      <SelectTrigger className="admin-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="admin-select-content">
                                        {[
                                          { value: 'instagram', label: 'Instagram' },
                                          { value: 'facebook', label: 'Facebook' },
                                          { value: 'twitter', label: 'Twitter/X' },
                                          { value: 'youtube', label: 'YouTube' },
                                          { value: 'linkedin', label: 'LinkedIn' },
                                          { value: 'tiktok', label: 'TikTok' },
                                          { value: 'vk', label: 'VK' },
                                          { value: 'viber', label: 'Viber' },
                                          { value: 'other', label: 'Другое' }
                                        ].map(platform => (
                                          <SelectItem key={platform.value} value={platform.value} className="admin-select-item">
                                            {platform.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>

                                    <Input
                                      placeholder="URL ссылки"
                                      value={link.url || ''}
                                      onChange={(e) => {
                                        const socialLinks = [...(block.content.social_links || [])];
                                        socialLinks[index] = { ...socialLinks[index], url: e.target.value };
                                        updateBlock(block.id, { content: { ...block.content, social_links: socialLinks } });
                                      }}
                                      className="admin-input"
                                    />

                                    <Input
                                      placeholder="Текст (необязательно)"
                                      value={link.label || ''}
                                      onChange={(e) => {
                                        const socialLinks = [...(block.content.social_links || [])];
                                        socialLinks[index] = { ...socialLinks[index], label: e.target.value };
                                        updateBlock(block.id, { content: { ...block.content, social_links: socialLinks } });
                                      }}
                                      className="admin-input"
                                    />
                                  </div>

                                  <button
                                    onClick={() => {
                                      const socialLinks = [...(block.content.social_links || [])];
                                      socialLinks.splice(index, 1);
                                      updateBlock(block.id, { content: { ...block.content, social_links: socialLinks } });
                                    }}
                                    className="p-2 rounded hover:bg-red-100"
                                    style={{ color: 'var(--admin-error)' }}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {block.type === 'quote' && (
                          <div className="space-y-2">
                            <Textarea
                              placeholder="Текст цитаты"
                              value={block.content.text || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, text: e.target.value } })}
                              rows={3}
                              className="admin-textarea"
                            />
                            <Input
                              placeholder="Автор (необязательно)"
                              value={block.content.author || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, author: e.target.value } })}
                              className="admin-input"
                            />
                          </div>
                        )}

                        {block.type === 'video' && (
                          <div className="space-y-2">
                            <Input
                              placeholder="YouTube embed URL (https://www.youtube.com/embed/...)"
                              value={block.content.url || ''}
                              onChange={(e) => updateBlock(block.id, { content: { url: e.target.value } })}
                              className="admin-input"
                            />
                            {block.content.url && (
                              <div className="aspect-video">
                                <iframe src={block.content.url} className="w-full h-full rounded" allowFullScreen />
                              </div>
                            )}
                          </div>
                        )}

                        {block.type === 'html' && (
                          <div>
                            <Textarea
                              placeholder="HTML код или iframe виджета"
                              value={block.content.code || ''}
                              onChange={(e) => updateBlock(block.id, { content: { code: e.target.value } })}
                              rows={6}
                              className="font-mono text-sm admin-textarea"
                            />
                          </div>
                        )}

                        {block.type === 'divider' && (
                          <div className="space-y-2">
                            <label className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>Стиль разделителя</label>
                            <select
                              value={block.content.style || 'solid'}
                              onChange={(e) => updateBlock(block.id, { content: { style: e.target.value } })}
                              className="admin-select w-full"
                            >
                              <option value="solid">Сплошная линия</option>
                              <option value="dashed">Пунктирная линия</option>
                              <option value="dotted">Точечная линия</option>
                            </select>
                            <hr style={{ borderStyle: block.content.style || 'solid' }} className="my-2" />
                          </div>
                        )}

                        {block.type === 'cards' && (
                          <div className="space-y-3">
                            <p style={{ color: 'var(--admin-text-secondary)' }}>Карточки (JSON формат)</p>
                            <Textarea
                              placeholder={`[{"title": "Заголовок", "text": "Текст", "icon": "Star"}]`}
                              value={JSON.stringify(block.content.items || [], null, 2)}
                              onChange={(e) => {
                                try {
                                  const items = JSON.parse(e.target.value);
                                  updateBlock(block.id, { content: { items } });
                                } catch { }
                              }}
                              rows={6}
                              className="font-mono text-sm admin-textarea"
                            />
                          </div>
                        )}

                        {block.type === 'accordion' && (
                          <div className="space-y-3">
                            <p style={{ color: 'var(--admin-text-secondary)' }}>Аккордеон (JSON формат)</p>
                            <Textarea
                              placeholder={`[{"title": "Вопрос", "content": "Ответ"}]`}
                              value={JSON.stringify(block.content.items || [], null, 2)}
                              onChange={(e) => {
                                try {
                                  const items = JSON.parse(e.target.value);
                                  updateBlock(block.id, { content: { items } });
                                } catch { }
                              }}
                              rows={6}
                              className="font-mono text-sm admin-textarea"
                            />
                          </div>
                        )}

                        {block.type === 'tarot_card' && (
                          <div className="space-y-2">
                            <Input
                              placeholder="Название карты"
                              value={block.content.card_name || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, card_name: e.target.value } })}
                              className="admin-input"
                            />
                            <Textarea
                              placeholder="Описание карты"
                              value={block.content.description || ''}
                              onChange={(e) => updateBlock(block.id, { content: { ...block.content, description: e.target.value } })}
                              rows={3}
                              className="admin-textarea"
                            />
                          </div>
                        )}

                        {block.type === 'astro_widget' && (
                          <div className="space-y-2">
                            <select
                              value={block.content.widget_type || 'moon_phase'}
                              onChange={(e) => updateBlock(block.id, { content: { widget_type: e.target.value } })}
                              className="admin-select w-full"
                            >
                              <option value="moon_phase">Фаза Луны</option>
                              <option value="zodiac_signs">Знаки Зодиака</option>
                              <option value="planetary_hours">Планетарные Часы</option>
                            </select>
                            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '0.875rem' }}>
                              Виджет будет отображать выбранную астрологическую информацию
                            </p>
                          </div>
                        )}

                        {block.type === 'calendar' && (
                          <div className="space-y-2">
                            <Input
                              placeholder="Заголовок календаря"
                              value={block.content.title || ''}
                              onChange={(e) => updateBlock(block.id, { content: { title: e.target.value } })}
                              className="admin-input"
                            />
                            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '0.875rem' }}>
                              Отобразит простой календарь для записи на консультацию
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHomePage;
